// Per-user UI preferences stored in localStorage. Keyed by userId so multiple
// accounts on the same browser don't pollute each other.
//
// Two kinds of state are tracked here:
//   1. logoutSnapshot — written only when the user clicks Logout. Holds the
//      pathname they were on so the next successful login restores it.
//      Token-expiry kicks and browser-close do NOT write this; that's by design.
//   2. dataGridState  — per-table { filterModel, sortModel, page, pageSize }.
//      Written continuously while the user interacts with the grid so the
//      restored route lands on the same view.

const SNAPSHOT_PREFIX = 'regailator_logoutSnapshot:';
const DATAGRID_PREFIX = 'regailator_dgState:';

const safeParse = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
};

const safeWrite = (key, value) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
        /* quota / disabled — drop silently */
    }
};

const safeRemove = (key) => {
    try {
        window.localStorage.removeItem(key);
    } catch (_) {
        /* ignore */
    }
};

// ---- logout snapshot -----------------------------------------------------

const snapshotKey = (userId) => (userId ? `${SNAPSHOT_PREFIX}${userId}` : null);

export const saveLogoutSnapshot = (userId, snapshot) => {
    const key = snapshotKey(userId);
    if (!key) return;
    safeWrite(key, { ...snapshot, ts: Date.now() });
};

export const getLogoutSnapshot = (userId) => {
    const key = snapshotKey(userId);
    if (!key) return null;
    return safeParse(window.localStorage.getItem(key));
};

export const clearLogoutSnapshot = (userId) => {
    const key = snapshotKey(userId);
    if (!key) return;
    safeRemove(key);
};

// ---- DataGrid state ------------------------------------------------------

const dgKey = (userId, tableName) =>
    userId && tableName ? `${DATAGRID_PREFIX}${userId}:${tableName}` : null;

export const getDataGridState = (userId, tableName) => {
    const key = dgKey(userId, tableName);
    if (!key) return null;
    return safeParse(window.localStorage.getItem(key));
};

export const setDataGridState = (userId, tableName, patch) => {
    const key = dgKey(userId, tableName);
    if (!key) return;
    const prev = safeParse(window.localStorage.getItem(key)) || {};
    safeWrite(key, { ...prev, ...patch });
};

// ---- Sidebar width ---------------------------------------------------------
// Remembers the width the user drags the sidebar to, so it's restored on next load
// instead of resetting to the default every time.

const SIDEBAR_WIDTH_PREFIX = 'regailator_sidebarWidth:';

const sidebarWidthKey = (userId) => (userId ? `${SIDEBAR_WIDTH_PREFIX}${userId}` : null);

export const getSidebarWidth = (userId) => {
    const key = sidebarWidthKey(userId);
    if (!key) return null;
    const stored = safeParse(window.localStorage.getItem(key));
    return typeof stored === 'number' && Number.isFinite(stored) ? stored : null;
};

export const setSidebarWidth = (userId, width) => {
    const key = sidebarWidthKey(userId);
    if (!key) return;
    safeWrite(key, width);
};

// ---- deliberate-logout marker ---------------------------------------------
// Set right before a user-initiated Logout clears the session, so PrivateRoute can tell
// "I just logged out on purpose" apart from a genuinely expired session — both currently look
// identical (no access token) from PrivateRoute alone, so Back after Logout re-showed the
// "session expired" toast. sessionStorage: scoped to this tab, gone on tab close.
const LOGOUT_MARKER_KEY = 'regailator_justLoggedOut';

// Cross-tab companion to the sessionStorage marker above. clearSession()'s cookie deletion is
// shared by every tab of the same browser, so a deliberate logout in one tab also drops every
// other open tab's session immediately — but sessionStorage is per-tab, so those other tabs'
// next PrivateRoute check found no marker and wrongly showed "session expired" for what was
// really a deliberate logout elsewhere. localStorage IS shared across tabs; a short TTL (rather
// than deleting on first read) avoids a race where only whichever tab reads it first suppresses
// the toast and every other tab still shows the false positive.
const CROSS_TAB_LOGOUT_KEY = 'regailator_justLoggedOut_broadcast';
const CROSS_TAB_LOGOUT_TTL_MS = 15000;

export const markDeliberateLogout = () => {
    try {
        window.sessionStorage.setItem(LOGOUT_MARKER_KEY, '1');
    } catch (_) {
        /* ignore */
    }
    try {
        window.localStorage.setItem(CROSS_TAB_LOGOUT_KEY, String(Date.now()));
    } catch (_) {
        /* ignore */
    }
};

// Consumes the same-tab marker (true at most once per logout) so a later genuine expiry on the
// same tab still shows the real toast, then falls back to the cross-tab marker (time-boxed
// rather than consumed, so it works for more than just the first other tab to check it).
export const consumeDeliberateLogoutMarker = () => {
    let was = false;
    try {
        was = window.sessionStorage.getItem(LOGOUT_MARKER_KEY) === '1';
        if (was) window.sessionStorage.removeItem(LOGOUT_MARKER_KEY);
    } catch (_) {
        was = false;
    }
    if (was) return true;
    try {
        const ts = Number(window.localStorage.getItem(CROSS_TAB_LOGOUT_KEY));
        if (ts && Date.now() - ts < CROSS_TAB_LOGOUT_TTL_MS) return true;
    } catch (_) {
        /* ignore */
    }
    return false;
};

// ---- has-ever-logged-in marker --------------------------------------------
// Set once, permanently, the first time this browser completes a login. Lets PrivateRoute tell
// a truly-expired session (had a token before) apart from a first-time visitor hitting a
// protected URL directly — both currently show the same "session expired" toast.
const EVER_LOGGED_IN_KEY = 'regailator_hasLoggedInBefore';

export const markEverLoggedIn = () => {
    try {
        window.localStorage.setItem(EVER_LOGGED_IN_KEY, '1');
    } catch (_) {
        /* ignore */
    }
};

export const hasEverLoggedIn = () => {
    try {
        return window.localStorage.getItem(EVER_LOGGED_IN_KEY) === '1';
    } catch (_) {
        return false;
    }
};
